import { useState } from 'react';
import { api, uploadDriverInvoicePdf, type DriverInvoicePdfRow } from '../lib/api';
import { ErrorBox } from '../components/DataTable';

/**
 * #408 기사개인오더 「세금계산서 파일 업로드」 — 스마트빌에서 받은 여러 장짜리 계산서 PDF 를 올리면
 * 쪽마다 「관리번호」를 읽어 발행번호와 짝짓고, 그 쪽만 떼어 신청 기사에게 알림톡(기존 계산서 창 알림톡과 같은 발송).
 * 서버가 보낼 수 있다고 판정한 쪽(ready)만 순서대로 보낸다 — 못 읽음·목록에 없음·금액 다름·이미 보냄·기사 못 정함은 사유만 보이고 안 보낸다.
 */

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const fmtPhone = (p: string) => p.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
type Sent = { ok: boolean; msg: string };

export function DriverInvoicePdfPanel({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [rows, setRows] = useState<DriverInvoicePdfRow[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<Record<number, Sent>>({});
  const [note, setNote] = useState<string | null>(null);

  async function pick(file: File | undefined, input: HTMLInputElement) {
    if (!file) return;
    input.value = '';
    setError(null); setNote(null); setSent({}); setRows(null); setFileName(file.name);
    setBusy('파일을 읽고 쪽마다 관리번호를 찾는 중…');
    let r: { rows: DriverInvoicePdfRow[]; inHours: boolean };
    try { r = await uploadDriverInvoicePdf(file); } catch (e) { setError(errMsg(e)); setBusy(null); return; }
    setRows(r.rows);
    const ready = r.rows.filter((x) => x.state === 'ready' && x.image && x.phone && x.invoiceId);
    if (ready.length === 0) { setBusy(null); setNote('보낼 수 있는 쪽이 없습니다 — 아래 사유를 확인해 주세요.'); return; }
    if (!r.inHours) { setBusy(null); setNote('알림톡은 09시~21시에만 보냅니다. 그 시간에 같은 파일을 다시 올려 주세요.'); return; }
    let ok = 0;
    for (const [i, x] of ready.entries()) {
      setBusy(`알림톡 보내는 중… (${i + 1}/${ready.length}) ${x.no}`);
      try {
        await api.sendDriverNotice({ target: 'invoice', id: x.invoiceId!, phone: x.phone!, image: x.image! });
        ok++; setSent((s) => ({ ...s, [x.page]: { ok: true, msg: `보냄 — ${fmtPhone(x.phone!)}` } }));
      } catch (e) {
        setSent((s) => ({ ...s, [x.page]: { ok: false, msg: errMsg(e) } }));
      }
    }
    setBusy(null);
    setNote(`${ready.length}건 중 ${ok}건 보냈습니다${ok < ready.length ? ` · 못 보낸 ${ready.length - ok}건은 사유를 확인해 주세요` : ''}.`);
    onDone();
  }

  const skipped = rows?.filter((x) => x.state === 'skip').length ?? 0;
  return (
    <div className="card max-h-[90vh] space-y-3 overflow-y-auto p-5 text-sm">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold">세금계산서 파일 업로드 — 기사 알림톡</h2>
        <button type="button" className="btn-ghost ml-auto" onClick={onClose} disabled={!!busy}>닫기</button>
      </div>
      <p className="text-xs text-ink-soft">
        스마트빌에서 받은 계산서 PDF(여러 장 가능)를 고르면 쪽마다 「관리번호」를 읽어 목록의 발행번호와 짝짓고,
        그 쪽만 떼어 신청 기사에게 바로 알림톡을 보냅니다. 번호를 못 읽었거나 목록에 없는 쪽, 금액이 다른 쪽, 이미 알림톡을 보낸 건은 보내지 않습니다.
      </p>
      <label className="block space-y-1">
        <span>계산서 PDF</span>
        <input className="field w-full" type="file" accept="application/pdf" disabled={!!busy}
               onChange={(e) => void pick(e.target.files?.[0], e.target)} />
      </label>
      {busy && <p className="text-brand-700">{busy}</p>}
      {error && <ErrorBox message={error} />}
      {note && <p className="rounded bg-amber-50 px-3 py-2 text-amber-900">{note}</p>}
      {rows && (
        <>
          <p className="text-xs text-ink-soft">{fileName} · {rows.length}장{skipped ? ` · 보내지 않는 쪽 ${skipped}장` : ''}</p>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-ink-soft"><th className="py-1">쪽</th><th>관리번호</th><th>거래처</th><th>신청 기사</th><th className="text-right">합계</th><th>결과</th></tr></thead>
            <tbody>
              {rows.map((x) => {
                const s = sent[x.page];
                const cls = s ? (s.ok ? 'text-emerald-700' : 'text-red-700') : x.state === 'skip' ? 'text-red-700' : 'text-ink-soft';
                const msg = s ? s.msg : x.state === 'skip' ? x.reason : busy ? '보낼 차례' : '보내지 않음';
                return (
                  <tr key={x.page} className="border-t">
                    <td className="py-1">{x.page}</td>
                    <td className="font-mono">{x.no ?? '—'}</td>
                    <td>{x.custName ?? '—'}</td>
                    <td>{x.requester ?? '—'}</td>
                    <td className="text-right">{x.total !== null ? x.total.toLocaleString('ko-KR') : '—'}</td>
                    <td className={cls}>{msg}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
